import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';
import useTablePagination from '../Hooks/useTablePagination';
import { formatTableTitle } from '../utils/formatTitle';
import { getStoredFilter } from '../utils/storedFilter';
import { getSafeSelectedValues } from '../utils/array';

function Organizations({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  const { getOrganizations } = useOrganizationApi();

  const [alertShown, setAlertShown] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [organizations, setOrganizations] = React.useState([]);
  const [totalOrganizations, setTotalOrganizations] = React.useState(0);

  const tableTitle = 'Organizations';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const filterName = `${formatTableTitle(tableTitle)}_filter`;
  const [selectedFilters, setSelectedFilters] = useState(() => getStoredFilter(filterName) ?? []);

  const filterOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Billed', value: 'billed' },
    { label: 'Not billed', value: 'not_billed' },
  ];

  const columns = [
    { name: 'Title', selector: (row) => row.title, sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Billing',
      selector: (row) => (row.is_billed === true ? 'Billed' : 'Not billed'),
      sortable: false,
    },
  ];

  React.useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${row.id}`);
  };

  const fetchOrganizations = async (skip, limit, filters = selectedFilters) => {
    const isActive =
      filters.includes('active') && !filters.includes('inactive')
        ? true
        : filters.includes('inactive') && !filters.includes('active')
          ? false
          : undefined;

    const isBilled =
      filters.includes('billed') && !filters.includes('not_billed')
        ? true
        : filters.includes('not_billed') && !filters.includes('billed')
          ? false
          : undefined;

    try {
      setLoading(true);
      const response = await getOrganizations(skip, limit, isActive, isBilled);

      setOrganizations(response.items);
      setTotalOrganizations(response.totalCount);
    } catch (error) {
      setError(error.message || 'Failed to fetch organizations!');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchOrganizations(storedSkip(), storedLimit());
  }, []);

  const handleFilterChange = async (skip, limit, filters) => {
    const safeFilters = getSafeSelectedValues(filterOptions, filters);

    localStorage.setItem(filterName, JSON.stringify(safeFilters));
    setSelectedFilters(safeFilters);

    await fetchOrganizations(skip, limit, safeFilters);
  };

  return (
    <div className="container">
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={organizations}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalOrganizations}
                dense={false}
                noTableHead={false}
                disabled={false}
                onRowClicked={handleRowClicked}
                rowKeyField="key"
                onChangePage={fetchOrganizations}
                getRowUrl={(row) => `/organizations/${row.id}`}
                SearchField
                onChangeFilter={handleFilterChange}
                typeSearch="multiselect"
                defaultValue={selectedFilters}
                searchOptions={filterOptions}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate('/new-organization')}
                >
                  Create New Organization
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OrganizationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <Organizations canEdit={canEdit} />
    </ConfirmationProvider>
  );
}

export default OrganizationsWrapper;
